import React, { Component, ErrorInfo, ReactNode } from "react";
import { logError } from "@/lib/errorLogger";
import { isStaleChunkError, mayReloadForStaleChunk, markStaleChunkReload } from "@/lib/errorReportPolicy";
import { BrandedErrorScreen } from "@/components/BrandedErrorScreen";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  getUserContext?: () => { user_id?: string; company_id?: string };
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  /** El fallo es un trozo de JavaScript caducado por un despliegue, no una caída. */
  staleChunk: boolean;
  /** Hay una recarga en camino: se pinta el fondo y se espera, sin mensaje. */
  reloading: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      staleChunk: false,
      reloading: false,
    };
  }

  /**
   * PURO, y solo decide qué se pinta.
   *
   * Antes recargaba la página y escribía en `sessionStorage` desde aquí. Esto es
   * fase de RENDER: React puede ejecutarlo dos veces o tirar su resultado, así
   * que era pedirle un efecto en el único sitio donde no puede prometer que se
   * haga una sola vez. La recarga se ha mudado a `componentDidCatch`, que es
   * fase de commit.
   *
   * Y devolvía `hasError: false` —«sigue pintando a los hijos»— para que no se
   * viera la pantalla de error mientras llegaba la recarga. Los hijos son los
   * que acaban de fallar: fallaban otra vez en el acto, y cuando un boundary no
   * consigue recuperarse React DESMONTA EL ÁRBOL ENTERO. Lo que queda entonces
   * es una raíz vacía: la pantalla en blanco con el color del fondo y sin
   * mensaje que reportó el usuario el 27-08-2026. Ahora se marca el error como
   * `staleChunk` y se pinta una pantalla estable mientras la recarga llega.
   */
  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, staleChunk: isStaleChunkError(error.message) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    /* Chunk caducado tras un despliegue: la pestaña tiene el `index.html` viejo
       y pide archivos con hashes que ya no existen. Se recarga UNA vez —con
       enfriamiento, para no acabar dando vueltas si el despliegue aún se está
       propagando— y no se reporta: no es un fallo de la aplicación y el usuario
       no tiene nada que hacer con él.
       Si la recarga NO se permite, se deja caer a la pantalla de error de
       siempre, que al menos explica y trae un botón. Quedarse en la pantalla
       estable sería dejar al usuario mirando un fondo liso para siempre. */
    if (isStaleChunkError(error.message)) {
      if (mayReloadForStaleChunk()) {
        markStaleChunkReload();
        this.setState({ reloading: true });
        window.location.reload();
      }
      return;
    }

    // Log error to backend
    const context = this.props.getUserContext?.() || {};

    logError(error, "ErrorBoundary", {
      error_type: "frontend",
      severity: "critical",
      user_id: context.user_id,
      company_id: context.company_id,
      details: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      staleChunk: false,
      reloading: false,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      /* Recargando por un chunk caducado: una pantalla ESTABLE del color del
         fondo, sin mensaje, durante el instante que tarda la recarga en llegar.
         No se vuelve a pintar a los hijos —volverían a fallar— y no se enseña la
         pantalla de error, porque no hay nada roto que contarle a nadie: hay una
         versión nueva y se está yendo a por ella. */
      if (this.state.reloading) {
        return <div className="min-h-screen bg-background" aria-hidden="true" />;
      }

      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <>
          <BrandedErrorScreen onRetry={this.handleReload} />
          {import.meta.env.DEV && this.state.error && (
            <details className="fixed bottom-2 left-2 right-2 max-w-3xl mx-auto text-left bg-muted/95 p-3 rounded-lg overflow-auto max-h-48 text-xs z-50">
              <summary className="cursor-pointer text-destructive font-mono">
                DEV: {this.state.error.message}
              </summary>
              {this.state.error.stack && (
                <pre className="text-muted-foreground mt-2 whitespace-pre-wrap">
                  {this.state.error.stack}
                </pre>
              )}
            </details>
          )}
        </>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
